/* EVAMATS — główna, wygląd „ambient” (właściciel 16.09).
   Moduł (type="module"): przeglądarka wykonuje go raz, nawet gdy dołączają go dwie sekcje.
   - setRoll(): przestawia liczbę na walcach (snippet evm-roll), gdy feed poda inną wartość;
   - <evm-google-rating>: po zdarzeniu load, w wolnej chwili i z niskim priorytetem pobiera ocenę z Reputon.
     Start ruchu gwiazdek i walców robi skrypt w snippecie zaraz po pierwszym malowaniu, bez czekania na ten plik;
   - <evm-odometer>: walce licznika kręcą się raz, gdy sekcja wejdzie w kadr.
   Reduced motion: od razu wartości końcowe. Brak JS: widać wartości z Liquid.
   17.09: reguły, które wcześniej były i tu, i w Liquid, mają teraz jednego właściciela — Liquid podaje je
   w atrybutach: data-evm-turns (dodatkowe obroty), data-evm-sep (odstęp tysięcy), data-nouns (opinia/opinie/
   opinii). Ten plik ich nie przelicza. */

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const NBSP = '\u00a0';
const STALE_DAYS = 45;
const FEED = 'https://grw.reputon.com/app/storefront/widget?shop=';

/* Separator tysięcy podaje Liquid (data-evm-sep) — tu go nie wybieramy. */
const fmt = (n, sep) => String(Math.max(0, Math.round(n))).replace(/\B(?=(\d{3})+(?!\d))/g, sep || NBSP);

/* Formy „opinia / opinie / opinii" przychodzą z Liquid w data-nouns; wybór formy to jedna reguła w jednym miejscu. */
function nounOf(nouns, n) {
  if (!nouns || nouns.length < 3) return '';
  if (n === 1) return nouns[0];
  const m10 = n % 10;
  const m100 = n % 100;
  return m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? nouns[1] : nouns[2];
}

/* Zdjęcie stanu startowego dopiero po jego namalowaniu i tylko w widocznej karcie
   (w karcie w tle klatki stoją — czekamy na powrót, wtedy ruch widać). */
function afterPaint(fn) {
  const go = () => requestAnimationFrame(() => requestAnimationFrame(fn));
  if (!document.hidden) {
    go();
    return;
  }
  document.addEventListener('visibilitychange', function onShow() {
    if (document.hidden) return;
    document.removeEventListener('visibilitychange', onShow);
    go();
  });
}

/* Prace drugiego planu: po zdarzeniu load, gdy przeglądarka ma wolną chwilę. */
function afterLoad(fn) {
  const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 600));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

/* Raz, gdy element wejdzie w kadr. */
function whenVisible(el, cb) {
  let fired = false;
  let io = null;
  const check = () => {
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.92 && r.bottom > 0) fire();
  };
  function fire() {
    if (fired) return;
    fired = true;
    if (io) io.disconnect();
    window.removeEventListener('scroll', check);
    window.removeEventListener('resize', check);
    cb();
  }
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) fire();
    }, { rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
  }
  window.addEventListener('scroll', check, { passive: true });
  window.addEventListener('resize', check);
}

/* ---------- liczby na walcach ---------- */

/* Ile dodatkowych obrotów dostaje cyfra licząc od prawej — listę podaje Liquid w data-evm-turns
   (np. „2,1"); poza listą zero. Reguła żyje w evm-roll.liquid, tutaj jest tylko odczytana. */
const turnsList = (roll) => (roll.dataset.evmTurns || '').split(',').map((x) => Number(x) || 0);
const turnsAt = (lista, fromRight) => (fromRight < lista.length ? lista[fromRight] : 0);

function digitSlot(digit, index, length, turns) {
  const slot = document.createElement('span');
  slot.className = `evm-roll__d evm-roll__d--c${turns}`;
  slot.style.setProperty('--p', String(turns * 10 + digit));
  slot.style.setProperty('--i', String(index));
  const glyph = document.createElement('span');
  glyph.className = 'evm-roll__g';
  glyph.textContent = String(digit);
  slot.appendChild(glyph);
  return slot;
}

/* Ta sama liczba cyfr: zmieniają się tylko pozycje, więc walce dojeżdżają do nowej wartości.
   Inna długość: nowy widok od razu na wartości końcowej. Obroty i odstęp tysięcy biorą się z atrybutów,
   które wypisał evm-roll.liquid — ten plik ich nie wybiera. */
function setRoll(roll, value) {
  const digits = String(Math.max(0, Math.round(value)));
  if (!roll || roll.dataset.evmRoll === digits) return;
  const lista = turnsList(roll);
  const sep = roll.dataset.evmSep || NBSP;
  const view = roll.querySelector('.evm-roll__view');
  const slots = view.querySelectorAll('.evm-roll__d');
  if (slots.length === digits.length) {
    slots.forEach((slot, i) => {
      const turns = turnsAt(lista, digits.length - 1 - i);
      slot.style.setProperty('--p', String(turns * 10 + Number(digits[i])));
      slot.firstElementChild.textContent = digits[i];
    });
  } else {
    const parts = [];
    for (let i = 0; i < digits.length; i += 1) {
      if (i > 0 && (digits.length - i) % 3 === 0) {
        const kropka = document.createElement('span');
        kropka.className = 'evm-roll__sep';
        kropka.dataset.sep = sep;
        parts.push(kropka);
      }
      parts.push(digitSlot(Number(digits[i]), i, digits.length, turnsAt(lista, digits.length - 1 - i)));
    }
    view.replaceChildren(...parts);
  }
  roll.querySelector('.evm-roll__sr').textContent = fmt(Number(digits), sep);
  roll.dataset.evmRoll = digits;
}

/* ---------- ocena Google ---------- */

class EvmGoogleRating extends HTMLElement {
  connectedCallback() {
    if (this.evmReady) return;
    this.evmReady = true;
    this.lang = this.dataset.lang === 'en' ? 'en' : 'pl';
    this.rating = parseFloat(this.dataset.rating) || 0;
    this.count = parseInt(this.dataset.count, 10) || 0;
    /* Formy słowa i odstęp tysięcy wypisał Liquid — tu tylko odczyt. */
    this.nouns = (this.dataset.nouns || '').split('|');
    this.sep = this.dataset.sep || NBSP;
    this.link = this.querySelector('[data-evm-rate-link]');
    if (this.link) this.link.addEventListener('click', (event) => this.goToWall(event));
    afterLoad(() => this.refresh());
  }

  render() {
    const value = this.lang === 'en' ? this.rating.toFixed(1) : this.rating.toFixed(1).replace('.', ',');
    const noun = nounOf(this.nouns, this.count);
    const valueEl = this.querySelector('[data-evm-rate-value]');
    const stars = this.querySelector('.evm-rate__stars');
    const nounEl = this.querySelector('[data-evm-rate-noun]');
    if (valueEl) valueEl.textContent = value;
    if (stars) stars.style.setProperty('--evm-fill', `${Math.max(0, Math.min(100, (this.rating / 5) * 100)).toFixed(1)}%`);
    setRoll(this.querySelector('[data-evm-rate-count] .evm-roll'), this.count);
    if (nounEl) nounEl.textContent = noun;
  }

  async refresh() {
    const shop = this.dataset.shop;
    if (!shop || !window.fetch) {
      this.staleCheck();
      this.render();
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    try {
      const res = await fetch(`${FEED}${encodeURIComponent(shop)}&lang=${this.lang}`, { credentials: 'omit', priority: 'low', signal: ctrl.signal });
      const data = res.ok ? await res.json() : null;
      const biz = data && data.business && data.business[0];
      const rating = biz ? Number(biz.rating) : NaN;
      const count = biz ? Number(biz.reviewsNumber) : NaN;
      if (!(rating >= 1 && rating <= 5) || !(count > 0)) throw new Error('brak oceny w odpowiedzi');
      this.rating = Math.round(rating * 10) / 10;
      this.count = Math.round(count);
      if (this.link && typeof biz.url === 'string' && /^https:\/\/maps\.google\.com\//.test(biz.url)) this.link.href = biz.url;
      this.classList.remove('evm-rate--no-count');
      this.dataset.evmFeed = 'ok';
    } catch (error) {
      this.dataset.evmFeed = 'error';
      this.staleCheck();
    } finally {
      clearTimeout(timer);
    }
    this.render();
  }

  /* Bez odpowiedzi feedu i ze starymi wartościami zapasowymi nie pokazujemy liczby opinii. */
  staleCheck() {
    const checked = Date.parse(this.dataset.checked || '');
    const stare = !checked || (Date.now() - checked) / 864e5 > STALE_DAYS;
    if (stare) this.classList.add('evm-rate--no-count');
    if (this.dataset.evmFeed !== 'error') this.dataset.evmFeed = stare ? 'stale' : 'zapas';
  }

  /* Na stronie jest ściana opinii Google — przewijamy do niej zamiast wychodzić do Map. 05.10 przegląd: główna v2
     ma karuzelę opinii (google_reviews_carousel) zamiast „ściany” — szukamy każdego bloku opinii Google. */
  goToWall(event) {
    const wall = document.querySelector('[id*="google_reviews_masonry_widget_block"], [id*="google_reviews_carousel"]');
    if (!wall) return;
    event.preventDefault();
    (wall.closest('.shopify-section') || wall).scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' });
  }
}

/* ---------- licznik przebiegu ---------- */

class EvmOdometer extends HTMLElement {
  /* 17.09: dawniej pytaliśmy o położenie od razu w connectedCallback. Moduł rusza przy readyState
     „interactive", gdy strona jeszcze się układa — raz na trzy wejścia na telefonie window.innerHeight
     dawało 3376 zamiast 844, sekcja wychodziła „już w kadrze" i walce stały od razu na końcu.
     Teraz decyduje pierwsze zgłoszenie IntersectionObserver: ono przychodzi po ułożeniu strony.
     Zastane w kadrze → wartości końcowe bez ruchu (jak dotąd); z dołu strony → walce kręcą się raz. */
  connectedCallback() {
    if (this.evmReady || REDUCE) return;
    this.evmReady = true;
    this.classList.add('evm-roll-pending');
    if (!('IntersectionObserver' in window)) {
      whenVisible(this, () => afterPaint(() => this.settle(false)));
      return;
    }
    let pierwsze = true;
    const io = new IntersectionObserver((entries) => {
      const widoczny = entries.some((entry) => entry.isIntersecting);
      if (pierwsze) {
        pierwsze = false;
        if (!widoczny) return;
        io.disconnect();
        this.settle(true);
        return;
      }
      if (!widoczny) return;
      io.disconnect();
      afterPaint(() => this.settle(false));
    }, { rootMargin: '0px 0px -8% 0px' });
    io.observe(this);
  }

  /* natychmiast = bez przejścia: zdejmujemy pauzę pod klasą, która wyłącza animację na jedną klatkę. */
  settle(natychmiast) {
    if (natychmiast) this.classList.add('evm-roll-instant');
    this.classList.remove('evm-roll-pending');
    if (natychmiast) afterPaint(() => this.classList.remove('evm-roll-instant'));
  }
}

if (!customElements.get('evm-google-rating')) customElements.define('evm-google-rating', EvmGoogleRating);
if (!customElements.get('evm-odometer')) customElements.define('evm-odometer', EvmOdometer);
